import { IMAGE_PLACEHOLDER_REGEX } from '@messenger/consts'

type RegExPatterns = {
  createWordBoundaryRegex: (pattern: string) => RegExp
  email: RegExp
  externalLink: RegExp
  protocolLink: RegExp
  urlPath: RegExp
  oneOrTwo: RegExp
  threeOrFour: RegExp
  fiveOrSix: RegExp
  lettersOnly: RegExp
  colorFormatIdentifier: RegExp
  // match a specific part of a URL, specifically the portion that follows the protocol (like http:// or https://
  urlDomainExtractor: RegExp
  // match URLs that start with either http:// or https://
  urlScheme: RegExp
  // match a specific URL path that includes the /auth/ segment followed by a 36-character string that consists of hexadecimal digits and hyphens
  authUUID: RegExp
  // match any uppercase letter from A to Z
  uppercaseLetter: RegExp
  // match single or double quotation marks in a string
  singleDoubleQuote: RegExp
  // match double quotation marks in a string
  doubleQuote: RegExp
  // match text that is enclosed within parentheses
  enclosedInParentheses: RegExp
  // Example:
  // const str = "This   is a   test.\nNew line here.";
  // const result = str.replace(/\s+/g, ' '); // Replaces multiple whitespace with a single space
  // console.log(result); // Output: "This is a test. New line here."
  multipleWhiteSpace: RegExp
  // matches hexadecimal color codes and RGBA color values
  // example matches #FFF, #FFFFFF, #123456, rgba(255,255,255,0.5), rgb(255,255,255)
  // example invalid matches #GGG (invalid hex code), rgb(255,0) (missing values), rgba(255,0,0,0,0) (too many values)
  validColor: RegExp
  // matches variable placeholders in a string like ${variable}
  dollarCurlyBraces: RegExp
  validInlineFontSize: RegExp
  validInlineTextAlign: RegExp
  // matches text that contains only commas and whitespace characters
  // ^ - Start of string
  // [\s]* - Zero or more whitespace characters
  // , - At least one comma (required)
  // [\s,]* - Zero or more whitespace characters or commas
  // $ - End of string
  onlyCommasAndWhitespace: RegExp
  // matches all whitespace characters including non-breaking spaces, zero-width spaces, etc.
  comprehensiveWhitespace: RegExp
  serviceAtIndex: RegExp
  // extracts the numeric index from paths like "value[1].default"
  uncheckedMatch: RegExp
  removeCommas: RegExp
  handleDayOfWeekDateSpanish: RegExp
  handleDayOfWeekDateFrench: RegExp
  handleMonthDayYearFrenchAndSpanish: RegExp
  ptValue: RegExp
  zeroWidthCharacters: RegExp
  multipleSpaces: RegExp
  validLetters: RegExp
  imagePlaceholder: RegExp
  // Pattern to detect duplicate S3 bucket paths in malformed URLs
  duplicateS3BucketPath: RegExp
  // Pattern to extract the second (correct) URL from malformed URL structure
  extractSecondUrlFromDuplicatedPath: RegExp
  // Pattern to match one or more trailing slashes at the end of a string
  trailingSlashes: RegExp
  // Pattern to match one or more leading slashes at the beginning of a string
  leadingSlashes: RegExp
  // Pattern to match whitespace between HTML tags (between > and <)
  whitespaceBetweenTags: RegExp
  // Pattern to match HTML break tags with optional whitespace and self-closing slash
  htmlBreakTag: RegExp
}

// TODO: Utilize this file for all regEx patterns in the app
export const regEx: RegExPatterns = {
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  externalLink: /((http|https):\/[\w\\.\\/\-=?#]+)/,
  protocolLink: /^(#|mailto:|tel:)/,
  urlPath: /[^/]+$/,
  urlScheme: /^https?:\/\//i,
  dollarCurlyBraces: /{\$([^}]*)}/g,
  validColor: /^(#[0-9A-Fa-f]{3,8}|rgba?\(.*\))$/,
  multipleWhiteSpace: /\s+/g,
  enclosedInParentheses: /\((.*?)\)/,
  singleDoubleQuote: /('|")/g,
  doubleQuote: /"/g,
  uppercaseLetter: /[A-Z]/g,
  authUUID: /\/auth\/([0-9a-fA-F-]{36})/,
  urlDomainExtractor: /:\/\/([^\\/]+)/,
  lettersOnly: /^[a-z]+$/i,
  colorFormatIdentifier: /^(#|rgb|rgba|hsl|hsla)/,
  oneOrTwo: /[1-2]/,
  threeOrFour: /[3-4]/,
  fiveOrSix: /[5-6]/,
  validInlineFontSize: /font-size\s*:\s*([^;]+);?/i,
  validInlineTextAlign: /text-align\s*:\s*([^;]+);?/i,
  onlyCommasAndWhitespace: /^[\s]*,[\s,]*$/,
  comprehensiveWhitespace:
    /[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]|\u200B|\u200C|\u200D|\uFEFF/,
  serviceAtIndex: /services\[\d+\]/,
  uncheckedMatch: /\[(\d+)\]/,
  removeCommas: /,/g,
  createWordBoundaryRegex: (word: string) => new RegExp(`\\b${word}\\b`, 'g'),
  handleDayOfWeekDateSpanish: /(\w+),\s+(\w+\.?)\s+(\d+),?\s+(\d{4})/g,
  handleDayOfWeekDateFrench: /(\w+),\s+(\w+)\s+(\d+),?\s+(\d{4})/g,
  handleMonthDayYearFrenchAndSpanish: /(\w+\.?)\s+(\d+),?\s+(\d{4})/g,
  ptValue: /^-?\d+(\.\d+)?pt$/,
  zeroWidthCharacters: /[\u200B-\u200D\uFEFF]/g,
  multipleSpaces: /\s+/g,
  validLetters: /[A-Za-z0-9]/,
  imagePlaceholder: IMAGE_PLACEHOLDER_REGEX,
  duplicateS3BucketPath: /https?:\/\/[^/]+(?:\/[^/]+)*\/https?:\/\//i,
  extractSecondUrlFromDuplicatedPath:
    /https?:\/\/[^/]+(?:\/[^/]+)*\/(https?:\/\/.+)/i,
  trailingSlashes: /\/+$/,
  leadingSlashes: /^\/+/,
  whitespaceBetweenTags: />\s+</g,
  htmlBreakTag: /<br\s*\/?>/i,
}

export default regEx
